import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { ToySprite } from '../objects/ToySprite';
import { NavigationBar } from '../objects/NavigationBar';
import { transitionManager } from '../systems/TransitionManager';
import { TOYS, getVisualForToy, getRarityColor } from '../config/toys';
import { CollectionSystem } from '../systems/CollectionSystem';
import { soundManager } from '../systems/SoundManager';
import type { ToyConfig } from '../types';

export class CollectionScene extends Phaser.Scene {
  private collection!: CollectionSystem;
  private filter: 'all' | 'common' | 'rare' | 'legendary' = 'all';
  private gridContainer!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'CollectionScene' });
  }

  create(): void {
    this.collection = new CollectionSystem();

    this.addBackground();
    this.addHeader();
    this.addNavigation();
    this.addStats();
    this.addFilters();
    this.addGrid();
  }

  private addBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }

    graphics.fillStyle(0xffdad8, 0.3);
    graphics.fillCircle(100, 600, 100);
    graphics.fillStyle(0x88f2e0, 0.2);
    graphics.fillCircle(1180, 120, 120);
  }

  private addHeader(): void {
    this.add.text(640, 130, 'My Sticker Book', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '40px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(640, 180, 'Your magical collection of cute toys', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '18px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5);
  }

  private addNavigation(): void {
    new NavigationBar(this, 0, 35, 'collection', (key) => this.handleNavigation(key));
  }

  private addStats(): void {
    const total = TOYS.length;
    const unlocked = this.collection.count();
    const pct = Math.round((unlocked / total) * 100);

    const containerX = 1100;
    const containerY = 55;

    this.add.text(containerX, containerY, `${unlocked}/${total}`, {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '28px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(1, 0.5);

    const barW = 200;
    const barH = 12;
    const barX = containerX - barW - 16;
    const barY = containerY - 6;

    const bgBar = this.add.rectangle(barX, barY, barW, barH, parseInt(DESIGN.colors.surfaceContainerHigh.replace('#', '0x'), 16));
    bgBar.setOrigin(0, 0.5);
    const fillBar = this.add.rectangle(barX, barY, barW * (pct / 100), barH, parseInt(DESIGN.colors.primaryContainer.replace('#', '0x'), 16));
    fillBar.setOrigin(0, 0.5);
  }

  private addFilters(): void {
    type FilterKey = 'all' | 'common' | 'rare' | 'legendary';
    const filters: Array<{ key: FilterKey; label: string }> = [
      { key: 'all', label: 'All' },
      { key: 'common', label: 'Common' },
      { key: 'rare', label: 'Rare' },
      { key: 'legendary', label: 'Legendary' },
    ];

    let x = 64;
    const y = 220;

    filters.forEach((f) => {
      const isActive = this.filter === f.key;
      const bgColor = isActive
        ? parseInt(DESIGN.colors.primary.replace('#', '0x'), 16)
        : parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16);
      const textColor = isActive ? '#ffffff' : DESIGN.colors.onSurface;

      const btn = this.add.container(x, y);
      const bg = this.add.rectangle(0, 0, 100, 40, bgColor).setInteractive({ useHandCursor: true });
      bg.setOrigin(0, 0.5);
      btn.add(bg);
      const label = this.add.text(50, 0, f.label, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '14px',
        color: textColor,
        fontStyle: 'bold',
      }).setOrigin(0.5);
      btn.add(label);

      bg.on('pointerdown', () => {
        this.filter = f.key;
        this.scene.restart();
      });

      x += 116;
    });
  }

  private addGrid(): void {
    this.gridContainer = this.add.container(0, 270);

    const toys = this.filter === 'all'
      ? TOYS
      : TOYS.filter((t) => t.rarity === this.filter);

    const cols = 5;
    const cardW = 200;
    const cardH = 240;
    const gapX = 24;
    const gapY = 24;
    const startX = (1280 - (cols * cardW + (cols - 1) * gapX)) / 2 + cardW / 2;

    toys.forEach((toy, index) => {
      const isUnlocked = this.collection.has(toy.id);
      const visual = getVisualForToy(toy.id);
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (cardW + gapX);
      const y = row * (cardH + gapY) + cardH / 2;

      const card = this.createCard(toy, visual, isUnlocked, x, y, cardW, cardH);
      this.gridContainer.add(card);
    });
  }

  private createCard(
    toy: ToyConfig,
    visual: ReturnType<typeof getVisualForToy>,
    isUnlocked: boolean,
    x: number,
    y: number,
    w: number,
    h: number
  ): Phaser.GameObjects.Container {
    const card = this.add.container(x, y);
    const rarityColor = getRarityColor(toy.rarity);

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.1);
    shadow.setOrigin(0.5);
    card.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, isUnlocked ? 1 : 0.6);
    bg.setOrigin(0.5);
    bg.setStrokeStyle(1, parseInt(rarityColor.replace('#', '0x'), 16), isUnlocked ? 1 : 0.3);
    card.add(bg);

    if (isUnlocked) {
      const glow = this.add.graphics();
      glow.lineStyle(2, parseInt(rarityColor.replace('#', '0x'), 16), 0.4);
      glow.strokeRoundedRect(-w / 2 + 6, -h / 2 + 6, w - 12, h - 12, 16);
      card.add(glow);

      const sprite = new ToySprite(this, 0, -30, visual, 90);
      card.add(sprite);

      card.add(this.add.text(0, 40, toy.name, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '16px',
        color: DESIGN.colors.onSurface,
        fontStyle: 'bold',
      }).setOrigin(0.5));

      card.add(this.add.text(0, 62, toy.rarity.toUpperCase(), {
        fontFamily: DESIGN.fonts.body,
        fontSize: '10px',
        color: rarityColor,
        fontStyle: 'bold',
      }).setOrigin(0.5));
    } else {
      const silhouette = new ToySprite(this, 0, -30, {
        ...visual,
        color: '#e0e0e0',
        faceColor: '#d0d0d0',
        accentColor: '#999999',
      }, 90);
      silhouette.setAlpha(0.5);
      card.add(silhouette);

      card.add(this.add.text(0, 40, '🔒', { fontSize: '28px' }).setOrigin(0.5));
      card.add(this.add.text(0, 75, '???', {
        fontFamily: DESIGN.fonts.body,
        fontSize: '14px',
        color: DESIGN.colors.onSurfaceVariant,
      }).setOrigin(0.5));
    }

    return card;
  }

  private handleNavigation(key: string): void {
    soundManager.play('click');
    switch (key) {
      case 'play':
        transitionManager.go(this, 'GameScene');
        break;
      case 'settings':
        transitionManager.go(this, 'SettingsScene');
        break;
      case 'analytics':
        transitionManager.go(this, 'AnalyticsDashboardScene');
        break;
      case 'debug':
        transitionManager.go(this, 'DebugPanelScene');
        break;
    }
  }
}
